/*@flow*/
/** @jsx jsx */
import type { Element } from 'react';
import { css, jsx } from '@emotion/core';
import { useState, useEffect } from 'react';
import useFiltersSectionsPanelType from './useFiltersSectionsPanelType';
import { Fields } from 'lib/proforma';
import LTCloseButton from './LTCloseButton';
import useCommands from './useCommands';
import { lightWhite } from './colors';
import useStoreState from 'lib/useStoreState';
import { clearStorage } from 'comps/City/useFilterLocalStorageSync';
import * as I from 'immutable';
import inIframe from 'lib/inIframe';
import inDistrictMap from 'lib/isEmbedded';

function useFilterSync(filters: {}) {
  const cmd = useCommands();
  useEffect(() => {
    cmd.updateGlobalFilters(filters);
  }, [filters]);
}

function useInitFilters() {
  const s = useStoreState({ started: '$queryInitialized', f: '$query.filter' });
  return s.started && s.f && s.f.toJS ? s.f.toJS() : {};
}

function LTFiltersBodySections() {
  let initFilters = useInitFilters();
  const [value, setValue] = useState({ filters: initFilters });
  // Synchronize with global filters
  useFilterSync(value.filters);
  const type = useFiltersSectionsPanelType(value.filters);

  function onClearFilters() {
    clearStorage();
    window.history.replaceState({}, '', '/map');
    setValue({ filters: {} });
  }

  const isEmbedded = inIframe() || inDistrictMap();

  const showClearBtn =
    isEmbedded === false &&
    (I: any).getIn(initFilters, ['type'], []).filter((v) => v !== 'foo')
      .length !== 0;

  return (
    <div data-testid="LTFiltersBodySections">
      <Fields value={value} type={type} onChange={(v) => setValue(v)} />
      {showClearBtn && (
        <button
          onClick={onClearFilters}
          data-testid="LTFiltersBodySectionsClearFilters"
          className="btn btn-link btn-block pt-4 pb-4"
          css={css(`color: #333;`)}
        >
          Clear filters
        </button>
      )}
    </div>
  );
}

function LTFiltersBody() {
  return (
    <div
      css={css(`
      .drawer-section {
        padding: 0 2rem;
        border-bottom: solid 1px #ccc;
      }

      .drawer-section.active {
        padding-bottom: 20px;
        .drawer-section-header > a {
          padding: 25px 0 10px;
        }
      }
      .drawer-section-content {
        h6 {
          font-weight: bold;
          text-transform: uppercase;
          margin-top: 2rem;
          margin-bottom: 0.5rem;
        }

        ::-webkit-input-placeholder {
          color: ${lightWhite};
        }

        .text-control {
          background: transparent;
          border: none;
          border-bottom: solid 1px ${lightWhite};
          border-radius: 0;
          color: ${lightWhite};
          padding: 0;
        }

        .year-control {
          -moz-appearance: textfield;
          &::-webkit-outer-spin-button,
          &::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }
        }

        .Select-control {
          background: transparent;
          border: none;
          border-bottom: solid 1px ${lightWhite};
          border-radius: 0;
          color: ${lightWhite};
          padding: 0;
          box-shadow: none;
        }

        .Select-placeholder,
        .Select-clear,
        .Select-value {
          color: ${lightWhite};
        }

        .Select-value {
          margin-left: 0;
          margin-right: 5px;
        }

        .Select-arrow {
          border-color: ${lightWhite} transparent transparent;
        }

        .Select-placeholder,
        .Select-input {
          padding: 0;
          margin-left: 0;
        }

        .Select-menu-outer {
          border-radius: 0;
          border: none;
        }

        .date-group {
          position: relative;
          .col-form-label:empty {
            margin-bottom: 23px;
            display: block;
          }
          .text-control {
            width: 100%;
          }
          .date-icon {
            position: absolute;
            right: 0;
            bottom: 8px;
            color: ${lightWhite};
          }
        }
      }

      .checkbox-item {
        font-size: 1.3rem;
        cursor: pointer;
        margin: 0.6rem 0 0.6rem;
        input, label { cursor: pointer; }
      }

      .drawer-section-header {
        font-size: 1.5rem;
        font-weight: bold;
        text-transform: uppercase;
        & > a {
          display: block;
          width: 100%;
          padding: 25px 0;
        }
      }

      .grid-col {
        margin-bottom: 15px;
      }


      input[type="date"]::-webkit-calendar-picker-indicator{
        color: rgba(0, 0, 0, 0);
        opacity: 1;
        display: block;
        background-image: url('data:image/svg+xml;utf8,${calendarSvg}');
        width: 20px;
        height: 20px;
        border-width: thin;
      }
    `)}
    >
      <LTFiltersBodySections />
    </div>
  );
}

const calendarSvg =
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -3 24 24" width="20px" height="20px"><style>path {fill: ${lightWhite};}</style><path d="M 6 1 L 6 3 L 5 3 C 3.9 3 3 3.9 3 5 L 3 19 C 3 20.1 3.9 21 5 21 L 19 21 C 20.1 21 21 20.1 21 19 L 21 5 C 21 3.9 20.1 3 19 3 L 18 3 L 18 1 L 16 1 L 16 3 L 8 3 L 8 1 L 6 1 z M 5 8 L 19 8 L 19 19 L 5 19 L 5 8 z"/></svg>`;

export default function LTFilters(): Element<string> {
  return (
    <div
      data-testid="LTFilters"
      css={css(`
      color: #333;
    `)}
    >
      <div className="p-4 d-flex border-bottom border-light-white justify-content-between align-items-center">
        <h5 className="text-uppercase m-0 font-weight-bold">MAP FILTERS</h5>
        <LTCloseButton color='#333' />
      </div>
      <LTFiltersBody />
    </div>
  );
}
